import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import blockingService from "../../services/blockingService";

interface BlockedSite {
  id: number;
  user_id: number;
  url: string;
  reason: string;
  category?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BlockedCategory {
  id: number;
  user_id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BlockingState {
  blockedSites: BlockedSite[];
  blockedCategories: BlockedCategory[];
  availableCategories: string[];
  stats: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: BlockingState = {
  blockedSites: [],
  blockedCategories: [],
  availableCategories: [],
  stats: null,
  isLoading: false,
  error: null,
};

// Fixed: Using default parameter value
export const fetchBlockedSites = createAsyncThunk(
  "blocking/fetchSites",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await blockingService.getBlockedSites(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch blocked sites"
      );
    }
  }
);

// Fixed: Using default parameter value
export const fetchBlockedCategories = createAsyncThunk(
  "blocking/fetchCategories",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await blockingService.getBlockedCategories(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch blocked categories"
      );
    }
  }
);

export const fetchBlockingStats = createAsyncThunk(
  "blocking/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await blockingService.getBlockingStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch blocking stats"
      );
    }
  }
);

export const addBlockedSite = createAsyncThunk(
  "blocking/addSite",
  async (
    data: { url: string; reason: string; category?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await blockingService.blockSite(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to block site"
      );
    }
  }
);

export const removeBlockedSite = createAsyncThunk(
  "blocking/removeSite",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await blockingService.unblockSite(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to unblock site"
      );
    }
  }
);

export const addBlockedCategory = createAsyncThunk(
  "blocking/addCategory",
  async (data: { name: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await blockingService.blockCategory(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to block category"
      );
    }
  }
);

export const removeBlockedCategory = createAsyncThunk(
  "blocking/removeCategory",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await blockingService.unblockCategory(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to unblock category"
      );
    }
  }
);

export const fetchAvailableCategories = createAsyncThunk(
  "blocking/fetchAvailableCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await blockingService.getAvailableCategories();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch available categories"
      );
    }
  }
);

const blockingSlice = createSlice({
  name: "blocking",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlockedSites.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBlockedSites.fulfilled, (state, action) => {
        state.isLoading = false;
        state.blockedSites = action.payload.data || [];
      })
      .addCase(fetchBlockedSites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchBlockedCategories.fulfilled, (state, action) => {
        state.blockedCategories = action.payload.blocked_categories || [];
        state.availableCategories = action.payload.available_categories || [];
      })

      .addCase(fetchBlockingStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })

      .addCase(addBlockedSite.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.blockedSites.unshift(action.payload.data);
        }
      })

      .addCase(removeBlockedSite.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.blockedSites = state.blockedSites.filter(
          (site) => site.id !== id
        );
      })

      .addCase(addBlockedCategory.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.blockedCategories.unshift(action.payload.data);
        }
      })

      .addCase(removeBlockedCategory.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.blockedCategories = state.blockedCategories.filter(
          (cat) => cat.id !== id
        );
      })
      .addCase(fetchAvailableCategories.fulfilled, (state, action) => {
        state.availableCategories = action.payload.categories || [];
      });
  },
});

export const { clearError } = blockingSlice.actions;
export default blockingSlice.reducer;
